<script setup lang="ts">
import { provide, toRef } from 'vue';
import { stepperContextKey } from './internal';
import { createStepperRegistry } from './useStepperRegistry';

const props = withDefaults(
  defineProps<{
    /** Opt-in navigation: a click on a done/active step emits the new index. */
    clickable?: boolean;
    /**
     * With `clickable`: true (default) only lets done steps and the active one be
     * clicked — no skipping ahead. False makes every step clickable, e.g. when
     * editing an existing record whose steps are all filled. Status (done/active/
     * open) still follows the active index.
     */
    linear?: boolean;
    /** Marker + label scale. Defaults to 'm'. */
    size?: 's' | 'm' | 'l';
  }>(),
  { clickable: false, linear: true, size: 'm' }
);

/** Zero-based index of the active step. */
const current = defineModel<number>({ required: true });

const registry = createStepperRegistry({
  current,
  clickable: toRef(props, 'clickable'),
  linear: toRef(props, 'linear'),
  onNavigate: (index) => {
    current.value = index;
  },
});

provide(stepperContextKey, registry);
</script>

<template>
  <ol class="nx-stepper" :class="`size-${size}`">
    <slot />
  </ol>
</template>

<style scoped>
.nx-stepper {
  display: flex;
  align-items: flex-start;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Scale is published as inherited custom properties consumed by NxStep. */
.nx-stepper.size-s {
  --nxt-stepper-marker-size: calc(var(--nxt-size-unit) * 1.5);
  --nxt-stepper-font-size: var(--nxt-font-size-xs);
}

.nx-stepper.size-m {
  --nxt-stepper-marker-size: calc(var(--nxt-size-unit) * 2);
  --nxt-stepper-font-size: var(--nxt-font-size-sm);
}

.nx-stepper.size-l {
  --nxt-stepper-marker-size: calc(var(--nxt-size-unit) * 2.75);
  --nxt-stepper-font-size: var(--nxt-font-size-md);
}
</style>
